import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * RB: Timer fuer das verzoegerte Schliessen.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #closeTimer = null;

  /**
   * RB: Schonfrist zwischen "Zeiger hat das Menue verlassen" und Schliessen.
   * Horizon schliesst sofort (der eigene JSDoc sagt "after a delay", der Code
   * macht es aber synchron). Jeder kurze Kontaktverlust – z.B. eine schraege
   * Mausbewegung, die den Menuepunkt seitlich verlaesst – liess das Panel
   * dadurch zuklappen. 150ms sind kurz genug, um nicht wie ein haengendes
   * Menue zu wirken, und lang genug fuer eine normale Mausbewegung.
   */
  static #CLOSE_DELAY_MS = 150;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#scheduleDeactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);

    // RB: Escape schliesst das Mega-Menue. Horizon oeffnet das Panel per Fokus
    // (on:focus="/activate"), bietet aber keinen Weg, es ohne Fokuswechsel
    // wieder zu schliessen.
    // Listener auf document, nicht auf dem Element: per Maus geoeffnet liegt der
    // Fokus weiterhin z.B. auf <body>, ein Listener am Menue bekaeme das Escape
    // nie zu sehen. WCAG 2.1 1.4.13 verlangt, dass Hover-Inhalte per Escape
    // verschwinden – vorher galt das nur fuer Tastaturbedienung.
    document.addEventListener('keydown', this.#escapeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('keydown', this.#escapeListener);
    this.#cancelScheduledDeactivate();
    this.#cleanupMutationObserver();
  }

  /**
   * RB: Escape schliesst das offene Panel und gibt den Fokus an den
   * ausloesenden Top-Level-Link zurueck.
   * @param {KeyboardEvent} event
   */
  #escapeListener = (event) => {
    if (event.key !== 'Escape') return;

    const item = this.#state.activeItem;
    if (!item) return;

    // Nur zurueckfokussieren, wenn der Fokus vorher im Menue lag. Bei einem per
    // Maus geoeffneten Panel wuerde ein focus() sonst den Fokus (und ggf. den
    // Scroll) unerwartet in den Header ziehen.
    const focusWasInMenu = this.contains(document.activeElement);

    event.preventDefault();
    // #close statt #deactivate: #deactivate steigt aus, solange der Zeiger auf
    // der Menueleiste liegt (overflowHovered – die <overflow-list> umfasst die
    // ganze Leiste). Genau das ist beim Escape aber der Normalfall, deshalb hat
    // Escape per Maus geoeffnet vorher nichts bewirkt.
    this.#close(item);

    if (!focusWasInMenu) return;

    const link = item.matches('a, button') ? item : item.querySelector('a, button');
    if (link instanceof HTMLElement) link.focus();
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // RB: Muss VOR dem Early-Return unten stehen. Wer in das offene Panel
    // (ein Kind des <li>) zurueckkehrt, loest activate mit demselben Item aus –
    // Horizon steigt dann sofort aus und der laufende Schliess-Timer wuerde
    // das Menue trotzdem zuklappen.
    this.#cancelScheduledDeactivate();

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      // RB: Horizon vergisst das hier – beim direkten Wechsel von einem
      // Menuepunkt zum naechsten laeuft #deactivate() nicht, und data-active
      // (= content-visibility: visible, sections/header.liquid:828-833) blieb
      // an jedem einmal geoeffneten Panel haengen.
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#scheduleDeactivate();
  }

  /**
   * RB: Schliesst das Panel erst nach einer Schonfrist. Kehrt der Zeiger
   * vorher in den Menuepunkt oder in das Panel zurueck, bricht activate()
   * den Timer ab und das Panel bleibt offen.
   */
  #scheduleDeactivate = () => {
    this.#cancelScheduledDeactivate();

    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.#deactivate();
    }, HeaderMenu.#CLOSE_DELAY_MS);
  };

  /**
   * RB: Bricht einen laufenden Schliess-Timer ab.
   */
  #cancelScheduledDeactivate = () => {
    if (this.#closeTimer == null) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.#cancelScheduledDeactivate();

    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.#close(item);
  };

  /**
   * RB: Das eigentliche Schliessen, ohne die Hover-Waechter aus #deactivate.
   * @param {HTMLElement} item
   */
  #close = (item) => {
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
